import { DateUtil, ModelRow } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutProcessCommissionsStepper } from "./autogen/AutogenLayoutProcessCommissionsStepper";
import {
    Button, Layout, LayoutLoadErrorEvent, PanelProcessLastRun, PanelProcessLastRunVariant, SlideoutDecorator, Step,
    StepEvent, Tab
} from "@mcleod/components";
import { BackOfficeReportPreview } from "../BackOfficeReportPreview";
import { ProcessCommissionsExceptionTable } from "./ProcessCommissionsExceptionTable";
import { CommonDialogs } from "@mcleod/common";
import { ProcessCommissionsPostExceptionTable } from "./ProcessCommissionsPostExceptionTable";

export interface CommissionsProcessStepperProps {
    startDate: Date,
    endDate: Date,
    payees: ModelRow[],
    processRecords: ModelRow[],
    refreshData: () => void,
    refreshOnClose?: boolean,
    processLog?: PanelProcessLastRun

}
export class ProcessCommissionsStepper extends AutogenLayoutProcessCommissionsStepper {
    private reportPreviewSummaries: BackOfficeReportPreview;
    private reportPostSummaries: BackOfficeReportPreview;

    static async showAsSlideout(props: CommissionsProcessStepperProps) {
        const stepperLayout = Layout.getLayout("lme/settlement/commissions/ProcessCommissionsStepper") as ProcessCommissionsStepper;
        const buttonContinue = stepperLayout.createContinueButton();
        const addlComponents = [];
        const processLog = PanelProcessLastRun.create("Commissions Processing",
            { variant: PanelProcessLastRunVariant.compact }
        );
        if(processLog) {
            props.processLog = processLog;
            addlComponents.push(processLog);
        }
        addlComponents.push(buttonContinue);

        SlideoutDecorator.decorateAndSlideIn(stepperLayout, {
            title: "Process Commissions",
            fillVerticalSpace: true,
            width: "60%",
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: addlComponents,
            layoutLoadListeners: () => {
                stepperLayout.stepperProcessCommissions.addBeforeStepListener((event: StepEvent) => {
                    stepperLayout.doBeforeStep(event, props, buttonContinue);
                });
                stepperLayout.setupParametersLayout(props);
                stepperLayout.setupCommissionsPostLayout(props, buttonContinue);
                stepperLayout.initializeSummariesPanel();
                stepperLayout.intializePostPanel();
            },
            onClose: () => {
                if (props.refreshOnClose) {
                    props.refreshData();
                }
                stepperLayout.slideOut();
            }
        });
    }

    private createContinueButton(): Button {
        return new Button({
            id: "buttonContinue",
            caption: "Continue",
            backgroundColor: "primary",
            color: "primary.reverse",
            rowBreak: false,
            borderWidth: 0,
            onClick: () => {
                this.advanceToNextStep();
            }
        });
    }

    advanceToNextStep() {
        this.stepperProcessCommissions.selectedIndex = this.stepperProcessCommissions.selectedIndex + 1;
    }

    async doBeforeStep(event: StepEvent, props: CommissionsProcessStepperProps, buttonContinue: Button) {

        if (event.oldIndex == this.stepParameters.index) {
            this.stepParameters.checked = true;
            props.startDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxBeginDate.text, true, false);
            props.endDate = DateUtil.parseDateWithKeywords(this.layoutParameters.textboxEndDate.text, true, false);
            const textboxPayees = this.layoutParameters.textboxPayee;
            props.payees = textboxPayees.isEmpty() ? null : textboxPayees.lookupModelData.map(row => row.get("id"));
        }

        if ( event.newIndex == this.stepSummaries.index) {
            if (this.stepSummaries.busy != true) {
                this.stepSummaries.busy = true;
                const response = await this.layoutParameters.printSummaries(props);
                props.refreshOnClose = true;
                this.setupWarningsAndExceptionsLayout().then((warningsAndExceptionsTable) => {
                    this.displaySummaryData(response, warningsAndExceptionsTable);
                })
                .finally(() => {
                    this.stepSummaries.checked = true;
                    this.stepSummaries.busy = false;
                });

            }
        }

        buttonContinue.visible = event.newIndex != this.stepPost.index;
    }

    private setupParametersLayout(props: CommissionsProcessStepperProps) {
        this.layoutParameters.addLayoutLoadListener(() => {
            this.layoutParameters.textboxPayee.addLookupModelSelectionListener((selectedRow) => {
                const selectedItems = this.layoutParameters.textboxPayee.selectedItems;
                if (selectedItems?.length > 0 && this.allRecordsHavePrinted(this.layoutParameters.textboxPayee.lookupModelData.map(row => row.get("id")), props.processRecords)) {
                    this.stepperProcessCommissions.components.forEach((component: Step) => component.visited = true);
                }
            });
        });
        if (props.payees.length > 0) {
            const payeeIds = props.payees.map(row => row.get("id"));
            this.layoutParameters.addLayoutLoadListener(() => {
                const textboxPayees = this.layoutParameters.textboxPayee;
                const modelRow = new ModelRow("");
                modelRow.set("payees", payeeIds.join(","));
                modelRow.setLookupModelData("payees", props.payees);
                textboxPayees.displayData(modelRow, null, 0);
            });
            if(this.allRecordsHavePrinted(payeeIds, props.processRecords)){
                this.stepperProcessCommissions.components.forEach((component: Step) => component.visited = true);
            }
        }
     }

     private allRecordsHavePrinted(payees: string[], records:ModelRow[]): boolean {
        return payees.every(payee =>
            records.every(record =>
                record.get("payee_id") !== payee || (!record.isNull("process_status") && record.get("process_status") === "S")
            )
        );
    }

    private setupCommissionsPostLayout(props: CommissionsProcessStepperProps, buttonContinue: Button) {
        this.layoutProcessCommissionsPost.addLayoutLoadListener(() => {

            this.layoutProcessCommissionsPost.buttonPost.addClickListener(async() => {
                const continueToPost = await CommonDialogs.showYesNo("Are you sure you would like to proceed?", "Post Confirmation", { parentElement: this.layoutProcessCommissionsPost._element });
                if (continueToPost) {
                    this.layoutProcessCommissionsPost.buttonPost.busy = true;
                    const response = await this.layoutProcessCommissionsPost.postCommissions(props);
                    this.layoutProcessCommissionsPost.buttonPost.busy = false;
                    props.refreshOnClose = true;
                    this.stepPost.checked = true;
                    this.setupPostExceptionsLayout().then((warningsAndExceptionsTable) => {
                        this.layoutProcessCommissionsPost.panelPost.visible = false;
                        this.displayPostData(response, warningsAndExceptionsTable);
                    });
                }
            });
        });
    }

    private initializeSummariesPanel() {
        this.reportPreviewSummaries = new BackOfficeReportPreview("Summaries");
        this.stepSummaries.add(this.reportPreviewSummaries);
    }

    private intializePostPanel(){
        this.reportPostSummaries = new BackOfficeReportPreview("Post Summaries");
        this.reportPostSummaries.visible = false;
        this.layoutProcessCommissionsPost.add(this.reportPostSummaries);
    }


    private setupWarningsAndExceptionsLayout():
        Promise<ProcessCommissionsExceptionTable> {
        return new Promise((resolve, reject) => {
            const layoutPath = "lme/settlement/commissions/ProcessCommissionsExceptionTable";
            const processCommissionsExceptionTable =
                Layout.getLayout(layoutPath) as ProcessCommissionsExceptionTable;
            processCommissionsExceptionTable.addLayoutLoadListener(() => {
                processCommissionsExceptionTable
                .addLoadErrorListener((event: LayoutLoadErrorEvent) => reject(event.errorMessage));
                resolve(processCommissionsExceptionTable);
            });
        });
    }

    private setupPostExceptionsLayout(): Promise<ProcessCommissionsPostExceptionTable> {
        return new Promise((resolve, reject) => {
            const layoutPath = "lme/settlement/commissions/ProcessCommissionsPostExceptionTable";
            const exceptionTable =
                Layout.getLayout(layoutPath) as ProcessCommissionsPostExceptionTable;
                exceptionTable.addLayoutLoadListener(() => {
                    exceptionTable
                .addLoadErrorListener((event: LayoutLoadErrorEvent) => reject(event.errorMessage));
                resolve(exceptionTable);
            });
        });
    }

    private displaySummaryData(postResponse: any, warningsAndExceptionsTable: ProcessCommissionsExceptionTable) {
        const exceptions = postResponse?.data[0]?.exceptions;
        if (exceptions) {
            warningsAndExceptionsTable.addWarningsAndExceptions(exceptions);
        }
        const nonReportTabs = this.getNonReportTabs(warningsAndExceptionsTable);
        const documents = [
            postResponse?.data[0]?.summary_data,
            postResponse?.data[0]?.totals_data,
            ...nonReportTabs,
            ...postResponse?.data[0].payee_summary_data
        ];
        this.reportPreviewSummaries.data = documents;
    }

    private displayPostData(postResponse: any, warningsAndExceptionsTable: ProcessCommissionsPostExceptionTable) {
        const exceptions = postResponse?.data[0]?.exceptions;
        if (exceptions) {
            warningsAndExceptionsTable.addWarningsAndExceptions(exceptions);
        }
        const nonReportTabs = this.getNonReportTabs(warningsAndExceptionsTable);
        const documents = [ postResponse?.data[0]?.totals_data, ...nonReportTabs ];
        this.reportPostSummaries.data = documents;
        this.reportPostSummaries.visible = true;
    }

    private getNonReportTabs(warningsAndExceptionsTable: ProcessCommissionsExceptionTable | ProcessCommissionsPostExceptionTable): Set<Tab> {
        const result = new Set<Tab>();
        if (!warningsAndExceptionsTable.tableExceptions.isEmpty()) {
            const tab = new Tab({ id: "exceptionsTab", paddingTop: 0 });
            const caption = "Exceptions";
            tab.caption = caption;
            tab.imageName = ImageName.triangleWarning;
            tab.imageColor = "error";
            tab.add(warningsAndExceptionsTable);
            result.add(tab);
        }
        return result;
    }


}
